"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type AlgorithmName = "SHA-256" | "SHA-1" | "SHA-384" | "SHA-512";

async function computeHash(
  algorithm: AlgorithmName,
  text: string
): Promise<string> {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest(algorithm, data);
  const bytes = new Uint8Array(digest);
  let hex = "";
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, "0");
  }
  return hex;
}

async function computeAllHashes(
  algorithms: AlgorithmName[],
  text: string
): Promise<Record<AlgorithmName, string>> {
  const entries = await Promise.all(
    algorithms.map(async (name) => {
      const hex = await computeHash(name, text);
      return [name, hex] as const;
    })
  );
  return Object.fromEntries(entries) as Record<AlgorithmName, string>;
}

/** English strings. A translated content file overrides these through `ui`. */
export const HASH_GENERATOR_STRINGS = {
  sha256Note: "Recommended — the modern standard",
  sha1Note: "Legacy checksums only — broken for security",
  sha384Note: "SHA-2 family, 384-bit digest",
  sha512Note: "SHA-2 family, 512-bit digest",
  textToHash: "Text to hash",
  inputPlaceholder: "The quick brown fox jumps over the lazy dog",
  generateHashes: "Generate Hashes",
  clear: "Clear",
  uppercaseHex: "Uppercase hex",
  hashingFailedError:
    "Hashing failed. Your browser may not support the Web Crypto API on this page (it requires a secure HTTPS context).",
  copied: "Copied!",
  copy: "Copy",
} as const;

export default function HashGeneratorWidget() {
  const [input, setInput] = useState("");
  const [hashes, setHashes] = useState<Record<AlgorithmName, string> | null>(
    null
  );
  const [uppercase, setUppercase] = useState(false);
  const [copiedAlgo, setCopiedAlgo] = useState<AlgorithmName | null>(null);
  const [error, setError] = useState("");

  const strings = useToolUi(HASH_GENERATOR_STRINGS);

  const algorithms: { name: AlgorithmName; note: string; primary: boolean }[] =
    [
      { name: "SHA-256", note: strings.sha256Note, primary: true },
      { name: "SHA-1", note: strings.sha1Note, primary: false },
      { name: "SHA-384", note: strings.sha384Note, primary: false },
      { name: "SHA-512", note: strings.sha512Note, primary: false },
    ];

  function formatHash(hex: string): string {
    return uppercase ? hex.toUpperCase() : hex;
  }

  function handleGenerate() {
    if (input.length === 0) {
      return;
    }
    computeAllHashes(
      algorithms.map((a) => a.name),
      input
    )
      .then((result) => {
        setHashes(result);
        setError("");
        setCopiedAlgo(null);
      })
      .catch(() => {
        setError(strings.hashingFailedError);
      });
  }

  function handleCopy(algo: AlgorithmName) {
    if (hashes === null) {
      return;
    }
    writeToClipboard(formatHash(hashes[algo])).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedAlgo(algo);
      setTimeout(() => setCopiedAlgo(null), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setHashes(null);
    setError("");
    setCopiedAlgo(null);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="hash-input"
        >
          {strings.textToHash}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="hash-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateHashes}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && hashes === null}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
        <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
          <input
            checked={uppercase}
            className="accent-primary"
            onChange={(e) => setUppercase(e.target.checked)}
            type="checkbox"
          />
          {strings.uppercaseHex}
        </label>
      </div>

      {error.length > 0 && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
          {error}
        </p>
      )}

      {/* Results */}
      {hashes !== null && (
        <section className="space-y-4">
          {algorithms.map((algo) => (
            <div
              className={
                algo.primary
                  ? "rounded-lg border-2 border-primary bg-card p-5"
                  : "rounded-lg border border-border bg-card p-5"
              }
              key={algo.name}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p
                    className={
                      algo.primary
                        ? "font-semibold text-foreground text-lg"
                        : "font-medium text-foreground"
                    }
                  >
                    {algo.name}
                  </p>
                  <p className="text-muted-foreground text-xs">{algo.note}</p>
                </div>
                <button
                  className="shrink-0 rounded-lg border border-border bg-card px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
                  onClick={() => handleCopy(algo.name)}
                  type="button"
                >
                  {copiedAlgo === algo.name ? strings.copied : strings.copy}
                </button>
              </div>
              <p
                className={
                  algo.primary
                    ? "mt-3 break-all font-mono text-base text-foreground"
                    : "mt-3 break-all font-mono text-muted-foreground text-sm"
                }
              >
                {formatHash(hashes[algo.name])}
              </p>
            </div>
          ))}
        </section>
      )}
    </>
  );
}
